import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Chip } from '@mui/material';
import { userApiClient } from '../../api/HttpClient';


interface TenantPaymentsDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
}

interface Payment {
  id: string;
  amount: number;
  due_date: string;
  status: 'pending' | 'paid'; // Explicitly define possible statuses
  paid_date?: string;
}

const TenantPaymentsDialog: React.FC<TenantPaymentsDialogProps> = ({ open, onClose, tenantId }) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      // Assuming an API endpoint to fetch payments for a tenant
      const client = await userApiClient;
      const response = await client.get<Payment[]>(`/api/pg_tenant/longterm/tenants/${tenantId}/payments/`);
      setPayments(response);
    } catch (err: any) {
      console.error('Error fetching payments:', err);
      setError(err.response?.data?.detail || 'Failed to fetch payments.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !tenantId) return;
    fetchPayments();
  }, [open, tenantId]);

  const handleMarkPaid = async (paymentId: string) => {
    try {
      const client = await userApiClient;
      await client.patch(`/api/pg_tenant/longterm/payments/${paymentId}/mark-paid/`);
      alert('Payment marked as paid!');
      fetchPayments(); // Refresh payments
    } catch (err: any) {
      console.error('Error marking payment paid:', err);
      alert(err.response?.data?.detail || 'Failed to mark payment paid.');
    }
  };

  const handleSendReminder = async () => {
    try {
      const client = await userApiClient;
      await client.post(`/api/pg_tenant/longterm/tenants/${tenantId}/remind-payment/`);
      alert('Payment reminder sent!');
    } catch (err: any) {
      console.error('Error sending reminder:', err);
      alert(err.response?.data?.detail || 'Failed to send reminder.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Payments for Tenant: {tenantId}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">Error: {error}</Typography>
        ) : payments.length === 0 ? (
          <Typography>No payments found for this tenant.</Typography>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
            <Table aria-label="payments table">
              <TableHead sx={{ bgcolor: 'primary.main' }}>
                <TableRow>
                  <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Due Date</TableCell>
                  <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Paid Date</TableCell>
                  <TableCell sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{payment.amount}</TableCell>
                    <TableCell>{payment.due_date}</TableCell>
                    <TableCell>
                      <Chip
                        label={payment.status.toUpperCase()}
                        color={payment.status === 'pending' ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{payment.paid_date || 'N/A'}</TableCell>
                    <TableCell>
                      {payment.status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleMarkPaid(payment.id)}
                          >
                            Mark Paid
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleSendReminder}
                          >
                            Send Reminder
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantPaymentsDialog;